import React, { useState, useMemo } from 'react';
import ImageLightbox from './ImageLightbox';
import SmartProductImage from './SmartProductImage';
import NumberPreviewThumb, { getPreviewFor, getPrintedNumberAttr } from './NumberPreviewThumb';
import { shopifyResize } from './previewImage';
import {
    EMBELLISHMENT_TYPES,
    getLineItemRecipe,
    handleMissingImage,
    handleWhiteBgDetection,
    MISSING_IMAGE_SVG,
} from './pressingHelpers';
import { useT } from './i18n/I18nContext';

export default function QCBundleDetail({
    orderName, orderClub, unit, group, bundlePickState,
    onBack, onPass, onFail, numberPreviews = {}, tpOverridesByName = null,
}) {
    // TP override wins over unit.playerNumber (which was extracted at packing-
    // portal load from the order's customAttributes / _soccer_order_info).
    const _tpOv = unit && unit.playerName && tpOverridesByName
        ? tpOverridesByName[unit.playerName] : null;
    const _isTpOverride = !!(_tpOv && String(_tpOv.override_number || '') !== String(unit.playerNumber || ''));
    const _displayNumber = _isTpOverride ? _tpOv.override_number : unit.playerNumber;
    const t = useT();
    const [lightboxOpen, setLightboxOpen]     = useState(false);
    const [lightboxKey, setLightboxKey]       = useState('front');
    const [lightboxImages, setLightboxImages] = useState([]);

    const parent = group?.items?.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || group?.items?.[0];

    const imgSlots = [];
    if (unit.previewFront) imgSlots.push({ key: 'front', label: t('Front'),     src: shopifyResize(unit.previewFront, 1200) });
    if (unit.previewBack)  imgSlots.push({ key: 'back',  label: t('Back side'), src: shopifyResize(unit.previewBack,  1200) });
    if (imgSlots.length === 0 && unit.imageUrl) {
        imgSlots.push({ key: 'image', label: '', src: unit.imageUrl });
    }
    if (imgSlots.length === 0) {
        imgSlots.push({ key: 'missing', label: '', src: MISSING_IMAGE_SVG });
    }

    const lightboxFrontBack = useMemo(
        () => imgSlots.map(s => ({ key: s.key, label: s.label || t('Image'), src: s.src })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [unit.previewFront, unit.previewBack, unit.imageUrl]
    );

    const openFrontBackLightbox = (key) => {
        setLightboxImages(lightboxFrontBack);
        setLightboxKey(key);
        setLightboxOpen(true);
    };

    const openEmbLightbox = (embLi) => {
        const src = embLi.image?.url || embLi.variant?.image?.url || MISSING_IMAGE_SVG;
        const label = embLi.productType || t('Item');
        setLightboxImages([{ key: 'emb', label, src }]);
        setLightboxKey('emb');
        setLightboxOpen(true);
    };

    const embellishments = group?.items?.filter(li => EMBELLISHMENT_TYPES.has(li.productType)) || [];

    const playerSummary = [unit.playerName, unit.playerNumber ? `#${unit.playerNumber}` : '', orderClub]
        .filter(Boolean).join(' · ');

    return (
        <div className="ppd">
            <div className="ppd__hdr">
                <div className="ppd__hdr-left">
                    <button className="ppd__back-btn" onClick={onBack}>{t('← Back to Board')}</button>
                    <div className="ppd__hdr-title">
                        <span className="ppd__order">{orderName}</span>
                        {playerSummary && <span className="ppd__sub"> · {playerSummary}</span>}
                    </div>
                </div>
            </div>

            <div className="ppd__content">
                <div className="ppd__left">
                    <div className="ppd__player-block">
                        <div>
                            <div className="ppd__player-title">{t('Now Inspecting')}</div>
                            <div className="ppd__player-name">{unit.playerName || unit.title}</div>
                            <div className="ppd__player-garment">{unit.title}</div>
                            {unit.variantTitle && (
                                <span className="ppd__player-variant">{unit.variantTitle}</span>
                            )}
                            {unit.sku && (
                                <span className="ppd__player-sku">{unit.sku}</span>
                            )}
                        </div>
                        {(_displayNumber != null && String(_displayNumber) !== '') && (
                            <div
                                className="ppd__player-number"
                                style={_isTpOverride ? { color: '#c1281c' } : undefined}
                                title={_isTpOverride ? `Team Portal Number Override (updated ${_tpOv.tp_updated_at || ''})` : undefined}
                            >
                                {_displayNumber}
                                {_isTpOverride && (
                                    <div style={{ color: '#c1281c', fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', marginTop: 6 }}>
                                        ⚠ TP Override
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="ppd__imgs-wrap">
                        <div className={`ppd__imgs-grid${imgSlots.length === 1 ? ' ppd__imgs-grid--single' : ''}`}>
                            {imgSlots.map(s => (
                                <div key={s.key} className="ppd__img" onClick={() => openFrontBackLightbox(s.key)}>
                                    <img
                                        src={s.src}
                                        alt={s.label || ''}
                                        draggable={false}
                                        onLoad={handleWhiteBgDetection}
                                        onError={handleMissingImage}
                                    />
                                    {s.label && <span className="ppd__img-label">{s.label}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="ppd__press-seq">
                    <div className="ppd__press-seq-title">
                        {t('Embellishments')} · {embellishments.length}
                    </div>
                    {embellishments.map((embLi, idx) => {
                        const type = (embLi.productType || '').toLowerCase();
                        const rowClass =
                            type === 'name'   ? 'ppd__row ppd__row--name' :
                            type === 'number' ? 'ppd__row ppd__row--number' :
                            'ppd__row ppd__row--logo';
                        const customAttr = (embLi.customAttributes || [])
                            .find(a => {
                                const k = (a.key || '').toLowerCase();
                                return k === 'name' || k === 'number' || k === 'logo' || k === 'value' || k === 'text';
                            });
                        const value = customAttr?.value || embLi.title || embLi.productType;
                        const placementAttr = (embLi.customAttributes || [])
                            .find(a => (a.key || '').toLowerCase() === 'placement');
                        const placement = placementAttr?.value || embLi.productType || '';
                        const recipe  = getLineItemRecipe(embLi);
                        const thumbSrc = embLi.image?.url || embLi.variant?.image?.url || MISSING_IMAGE_SVG;

                        const embPickState = (bundlePickState?.embPickStates || [])
                            .find(e => e.lineItem?.id === embLi.id);
                        const isUnpicked = !!embPickState?.isUnpicked;

                        return (
                            <div key={embLi.id || idx} className={rowClass}>
                                <div className="ppd__row-info">
                                    <span className="ppd__row-placement">{placement}</span>
                                    {isUnpicked && (
                                        <span className="ppd__row-unpicked">{t('Unpicked')}</span>
                                    )}
                                    <div className="ppd__row-value">{value}</div>
                                    {embLi.sku && <div className="ppd__row-sku">{embLi.sku}</div>}
                                    {embLi.variant?.title && embLi.variant.title !== 'Default Title' && (
                                        <div className="ppd__row-variant">{embLi.variant.title}</div>
                                    )}
                                    <div className="ppd__row-recipe">
                                        {recipe.temp && (
                                            <span className="ppd__chip"><span className="ppd__chip-lbl">{t('Temp')}</span><span className="ppd__chip-val">{recipe.temp}</span></span>
                                        )}
                                        {recipe.pressure && (
                                            <span className="ppd__chip"><span className="ppd__chip-lbl">{t('Pressure')}</span><span className="ppd__chip-val">{recipe.pressure}</span></span>
                                        )}
                                        {recipe.time && (
                                            <span className="ppd__chip"><span className="ppd__chip-lbl">{t('Time')}</span><span className="ppd__chip-val">{recipe.time}</span></span>
                                        )}
                                        {recipe.peel && (
                                            <span className="ppd__chip"><span className="ppd__chip-lbl">{t('Peel')}</span><span className="ppd__chip-val">{recipe.peel}</span></span>
                                        )}
                                    </div>
                                </div>
                                <div
                                    className="ppd__row-thumb"
                                    onClick={(e) => { e.stopPropagation(); openEmbLightbox(embLi); }}
                                >
                                    {(() => {
                                        const preview = getPreviewFor(embLi, numberPreviews);
                                        const num = getPrintedNumberAttr(embLi.customAttributes)
                                            || getPrintedNumberAttr(group?.items?.[0]?.customAttributes)
                                            || preview?.defaultValue || '';
                                        if (preview && num) {
                                            return <NumberPreviewThumb preview={preview} number={num} size={88} borderColor="transparent" />;
                                        }
                                        return (
                                            <SmartProductImage
                                                src={thumbSrc}
                                                alt=""
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="qcb-detail-actions">
                <button className="qcb-detail-pass" onClick={onPass}>
                    ✓ {t('Pass QC')}
                </button>
                <button className="qcb-detail-fail" onClick={onFail}>
                    ✗ {t('Fail QC')}
                </button>
            </div>

            <ImageLightbox
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                images={lightboxImages}
                initialKey={lightboxKey}
            />
        </div>
    );
}
